import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import MultiSelectFilter, { CASE_STATUS_PRESETS, SEVERITY_PRESETS } from '../components/common/MultiSelectFilter';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  AutoAwesome as AIIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  FolderOpen as CaseIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePermission } from '../hooks/usePermission';
import { useTableSort } from '../hooks/useTableSort';
import usePersistedFilters from '../hooks/usePersistedFilters';
import SortableHeaderCell from '../components/common/SortableHeaderCell';
import { formatDateTime } from '../utils/dateUtils';
import { SlaBadge } from '../components/Cases/SlaBadge';

const DEFAULT_SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const DEFAULT_STATUSES = ['Open', 'In Progress', 'Under Review', 'Resolved', 'Closed'];
const SEARCH_DEBOUNCE_MS = 400;
const CASES_FILTER_DEFAULTS = {
  search: '', status: [], severity: [], category: '', assignedTo: '', sort: '-createdAt', rowsPerPage: 20,
};

const emptyForm = {
  title: '',
  description: '',
  severity: 'Medium',
  category: '',
  priority: 'Medium',
  assignedTo: '',
};

const userDisplayName = (u) => {
  if (!u) return 'Unassigned';
  if (typeof u === 'string') return u;
  const name = `${u.firstName || ''} ${u.lastName || ''}`.trim();
  return name || u.username || 'Unassigned';
};

export const Cases = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getSeverityColor, getStatusColor, getFormattedStatuses } = useSettings();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission('cases', 'create');

  const { filters, setFilter, reset: resetFilters } = usePersistedFilters('cases', CASES_FILTER_DEFAULTS);

  // Data
  const [cases, setCases] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Options
  const [options, setOptions] = useState({
    statuses: DEFAULT_STATUSES,
    severities: DEFAULT_SEVERITIES,
    categories: [],
    priorities: DEFAULT_SEVERITIES,
    teamRoles: [],
  });
  const [users, setUsers] = useState([]);

  // Filters / paging (page is 0-indexed for TablePagination, not persisted)
  const searchTerm = filters.search;
  const setSearchTerm = (value) => setFilter('search', value);
  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm.trim());
  const statusFilter = filters.status; // multi-select
  const setStatusFilter = (value) => setFilter('status', value);
  const severityFilter = filters.severity;
  const setSeverityFilter = (value) => setFilter('severity', value);
  const categoryFilter = filters.category;
  const setCategoryFilter = (value) => setFilter('category', value);
  const assignedToFilter = filters.assignedTo;
  const setAssignedToFilter = (value) => setFilter('assignedTo', value);
  const [page, setPage] = useState(0);
  const rowsPerPage = filters.rowsPerPage;
  const setRowsPerPage = (value) => setFilter('rowsPerPage', value);
  const { sort, field: sortField, direction: sortDirection, toggle: toggleSort, setSort } = useTableSort(filters.sort);

  // Keep the sort control and the persisted filters in sync in both
  // directions: adopt filters.sort when it changes externally (profile
  // hydration, "Clear filters"), otherwise persist local sort changes
  // made via column headers.
  const lastSyncedSort = useRef(filters.sort);
  useEffect(() => {
    if (sort === filters.sort) {
      lastSyncedSort.current = sort;
      return;
    }
    if (filters.sort !== lastSyncedSort.current) {
      lastSyncedSort.current = filters.sort;
      setSort(filters.sort);
    } else {
      lastSyncedSort.current = sort;
      setFilter('sort', sort);
    }
  }, [sort, filters.sort, setFilter, setSort]);

  // Create dialog
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [dialogError, setDialogError] = useState(null);

  // Correlation status modal state
  const [correlationModalOpen, setCorrelationModalOpen] = useState(false);
  const [correlationStatus, setCorrelationStatus] = useState({
    loading: false,
    success: false,
    error: null,
    results: null,
  });

  const hasFilters = Boolean(debouncedSearch || statusFilter.length || severityFilter.length || categoryFilter || assignedToFilter);

  // Status options: prefer configurable enums, fall back to server list
  const statusOptions = useMemo(() => {
    const formatted = getFormattedStatuses('case');
    if (formatted && formatted.length > 0) return formatted;
    return options.statuses.map((s) => ({ value: s, label: s }));
  }, [getFormattedStatuses, options.statuses]);

  // Debounce search input
  useEffect(() => {
    const handle = setTimeout(() => {
      setPage(0);
      setDebouncedSearch(searchTerm.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Sorting a different column (or flipping direction) resets to page 1
  useEffect(() => {
    setPage(0);
  }, [sort]);

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { page: page + 1, limit: rowsPerPage, sort };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (severityFilter.length) params.severity = severityFilter.join(',');
      if (categoryFilter) params.category = categoryFilter;
      if (assignedToFilter) params.assignedTo = assignedToFilter;

      const response = await apiService.cases.getAll(params);
      setCases(response.data.cases || []);
      setPagination(response.data.pagination || { page: page + 1, limit: rowsPerPage, total: 0, pages: 0 });
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError(err.response?.data?.message || 'Failed to load cases. Please try again.');
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [page, rowsPerPage, sort, debouncedSearch, statusFilter, severityFilter, categoryFilter, assignedToFilter]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Load option lists and users once
  useEffect(() => {
    let cancelled = false;
    apiService.cases.getOptions()
      .then((res) => {
        if (cancelled || !res.data) return;
        setOptions((prev) => ({ ...prev, ...res.data }));
        setFormData((prev) => (prev.category ? prev : { ...prev, category: res.data.categories?.[0] || '' }));
      })
      .catch((err) => console.error('Error loading case options:', err));
    apiService.users.getAll({ limit: 100 })
      .then((res) => { if (!cancelled) setUsers(res.data.users || []); })
      .catch((err) => console.error('Error loading users:', err));
    return () => { cancelled = true; };
  }, []);

  const formatCaseDateTime = (dateString) => formatDateTime(dateString, user?.preferences);

  const handleOpenModal = () => {
    setDialogError(null);
    setFormData({ ...emptyForm, category: options.categories?.[0] || '' });
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (submitting) return;
    setOpenModal(false);
    setDialogError(null);
    setFormData({ ...emptyForm, category: options.categories?.[0] || '' });
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setDialogError(null);
      const payload = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        severity: formData.severity,
        priority: formData.priority,
        category: formData.category,
      };
      if (formData.assignedTo) payload.assignedTo = formData.assignedTo;
      await apiService.cases.create(payload);
      setOpenModal(false);
      setFormData({ ...emptyForm, category: options.categories?.[0] || '' });
      await fetchCases();
    } catch (err) {
      console.error('Error creating case:', err);
      const data = err.response?.data;
      const validation = Array.isArray(data?.errors) ? data.errors.map((e) => e.msg || e.message).filter(Boolean).join('; ') : '';
      setDialogError(data?.message || validation || 'Failed to create case. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewCase = (caseItem) => {
    navigate(`/dashboard/cases/${caseItem._id}`);
  };

  const handleClearFilters = () => {
    setPage(0);
    resetFilters();
    setDebouncedSearch('');
  };

  const handleCaseCorrelation = async () => {
    setCorrelationModalOpen(true);
    setCorrelationStatus({ loading: true, success: false, error: null, results: null });

    try {
      const response = await apiService.ai.correlateCases(true);
      setCorrelationStatus({ loading: false, success: true, error: null, results: response.data });
      if (response.data.casesCreated > 0) {
        fetchCases();
      }
    } catch (err) {
      console.error('Error correlating cases:', err);
      setCorrelationStatus({
        loading: false,
        success: false,
        error: err.response?.data?.message || err.message || 'Failed to analyze case correlations',
        results: null,
      });
    }
  };

  const handleCloseCorrelationModal = () => {
    setCorrelationModalOpen(false);
    setCorrelationStatus({ loading: false, success: false, error: null, results: null });
  };

  const renderSeverityChip = (value) => {
    const color = getSeverityColor(value);
    return (
      <Chip
        label={value || '-'}
        size="small"
        sx={{
          backgroundColor: color?.main || '#757575',
          color: color?.contrastText || '#fff',
        }}
      />
    );
  };

  const renderStatusChip = (value) => {
    const color = getStatusColor(value);
    const colorString = typeof color === 'string' ? color : color?.main || '#757575';
    return (
      <Chip
        label={value || '-'}
        size="small"
        variant="outlined"
        sx={{ borderColor: colorString, color: colorString }}
      />
    );
  };

  if (!hasLoaded && loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const showEmptyNoCases = !loading && !error && pagination.total === 0 && !hasFilters;
  const showEmptyFiltered = !loading && !error && pagination.total === 0 && hasFilters;

  const handleExportCsv = async () => {
    const params = new URLSearchParams();
    Object.entries({ status: statusFilter.join(','), severity: severityFilter.join(','), category: categoryFilter, assignedTo: assignedToFilter }).forEach(([k, v]) => { if (v) params.set(k, v); });
    const qs = params.toString();
    try {
      await apiService.download(`/cases/export${qs ? `?${qs}` : ''}`, `cases-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) {
      console.error('Export failed:', err);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Cases
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Manage security incidents and investigations
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCsv}>
            Export CSV
          </Button>
          <Button
            variant="outlined"
            startIcon={<AIIcon />}
            onClick={handleCaseCorrelation}
            disabled={loading}
          >
            AI Case Correlation
          </Button>
          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
            >
              New Case
            </Button>
          )}
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={<Button color="inherit" size="small" onClick={fetchCases}>Retry</Button>}
        >
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Filters */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by title, description, or case number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MultiSelectFilter label="Status" value={statusFilter} onChange={(v) => { setPage(0); setStatusFilter(v); }} options={statusOptions} presets={CASE_STATUS_PRESETS} allLabel="All statuses" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <MultiSelectFilter label="Severity" value={severityFilter} onChange={(v) => { setPage(0); setSeverityFilter(v); }} options={options.severities} presets={SEVERITY_PRESETS} allLabel="All severities" />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => { setPage(0); setCategoryFilter(e.target.value); }}
                >
                  <MenuItem value="">All Categories</MenuItem>
                  {options.categories.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Assignee</InputLabel>
                <Select
                  value={assignedToFilter}
                  label="Assignee"
                  onChange={(e) => { setPage(0); setAssignedToFilter(e.target.value); }}
                >
                  <MenuItem value="">All Assignees</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>{userDisplayName(u)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Cases Table */}
          <TableContainer sx={{ position: 'relative' }}>
            {loading && (
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={1}
                sx={{ backgroundColor: 'rgba(255,255,255,0.6)' }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
            <Table>
              <TableHead>
                <TableRow>
                  <SortableHeaderCell field="caseNumber" label="Case Number" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="title" label="Title" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="severity" label="Severity" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="status" label="Status" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="sla.resolveBy" label="SLA" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="category" label="Category" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="priority" label="Priority" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="assignedTo.lastName" label="Assigned To" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="detectionCount" label="Detections" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <SortableHeaderCell field="createdAt" label="Created" sortField={sortField} direction={sortDirection} onSort={toggleSort} />
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cases.map((caseItem) => (
                  <TableRow
                    key={caseItem._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewCase(caseItem)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        {caseItem.caseNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {caseItem.title}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderSeverityChip(caseItem.severity)}</TableCell>
                    <TableCell>{renderStatusChip(caseItem.status)}</TableCell>
                    <TableCell><SlaBadge sla={caseItem.sla} status={caseItem.status} compact /></TableCell>
                    <TableCell>{caseItem.category || '-'}</TableCell>
                    <TableCell>{renderSeverityChip(caseItem.priority)}</TableCell>
                    <TableCell>{userDisplayName(caseItem.assignedTo)}</TableCell>
                    <TableCell>
                      <Chip
                        label={Array.isArray(caseItem.detections) ? caseItem.detections.length : (caseItem.detectionsCount || 0)}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>{formatCaseDateTime(caseItem.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => { e.stopPropagation(); handleViewCase(caseItem); }}
                        title="View case details"
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {showEmptyNoCases && (
            <Box textAlign="center" py={6}>
              <CaseIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                No cases yet
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Cases group related detections into a single investigation. Create your first case to get started.
              </Typography>
              {canCreate && (
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenModal}>
                  Create Case
                </Button>
              )}
            </Box>
          )}

          {showEmptyFiltered && (
            <Box textAlign="center" py={4}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No cases match your filters.
              </Typography>
              <Button size="small" onClick={handleClearFilters}>Clear filters</Button>
            </Box>
          )}

          {pagination.total > 0 && (
            <TablePagination
              component="div"
              count={pagination.total}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Case Modal */}
      <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>Create New Case</DialogTitle>
        <DialogContent>
          {dialogError && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setDialogError(null)}>
              {dialogError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                inputProps={{ maxLength: 200 }}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={3}
                inputProps={{ maxLength: 5000 }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Severity</InputLabel>
                <Select
                  value={formData.severity}
                  label="Severity"
                  onChange={(e) => handleFormChange('severity', e.target.value)}
                >
                  {options.severities.map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleFormChange('priority', e.target.value)}
                >
                  {options.priorities.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleFormChange('category', e.target.value)}
                >
                  {options.categories.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Assign To</InputLabel>
                <Select
                  value={formData.assignedTo}
                  label="Assign To"
                  onChange={(e) => handleFormChange('assignedTo', e.target.value)}
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u._id} value={u._id}>{userDisplayName(u)}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} disabled={submitting}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.title.trim() || !formData.description.trim() || !formData.category}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {submitting ? 'Creating...' : 'Create Case'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Case Correlation Status Modal */}
      <Dialog
        open={correlationModalOpen}
        onClose={correlationStatus.loading ? undefined : handleCloseCorrelationModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {correlationStatus.loading && <CircularProgress size={24} />}
          {correlationStatus.success && <SuccessIcon color="success" />}
          {correlationStatus.error && <ErrorIcon color="error" />}
          AI Case Correlation
          {!correlationStatus.loading && (
            <IconButton
              onClick={handleCloseCorrelationModal}
              sx={{ ml: 'auto' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent>
          {correlationStatus.loading && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={48} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Analyzing Detections...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                AI is analyzing open detections for correlation patterns and creating cases automatically.
              </Typography>
            </Box>
          )}

          {correlationStatus.success && correlationStatus.results && (
            <Box>
              <Alert severity="success" sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  AI Case Correlation completed successfully
                </Typography>
              </Alert>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="primary" gutterBottom>
                      {correlationStatus.results.detectionsAnalyzed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Detections Analyzed
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="warning.main" gutterBottom>
                      {correlationStatus.results.caseSuggestions?.length || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Correlations Found
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" color="success.main" gutterBottom>
                      {correlationStatus.results.casesCreated || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cases Created
                    </Typography>
                  </Card>
                </Grid>
              </Grid>

              {correlationStatus.results.casesCreated > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Created Cases
                  </Typography>
                  {correlationStatus.results.createdCaseIds?.map((caseId, index) => (
                    <Chip
                      key={caseId}
                      label={`Case ${index + 1}: ${caseId}`}
                      color="primary"
                      variant="outlined"
                      onClick={() => navigate(`/dashboard/cases/${caseId}`)}
                      sx={{ mr: 1, mb: 1 }}
                    />
                  ))}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    The case list has been refreshed to show the new cases.
                  </Typography>
                </Box>
              )}

              {correlationStatus.results.caseSuggestions?.length > 0 && correlationStatus.results.casesCreated === 0 && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    Found {correlationStatus.results.caseSuggestions.length} potential correlations, but they didn't meet the confidence threshold for automatic case creation.
                  </Typography>
                </Alert>
              )}

              {correlationStatus.results.caseSuggestions?.length === 0 && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  <Typography variant="body2">
                    No correlation patterns found in the current open detections. This could mean:
                  </Typography>
                  <ul style={{ marginTop: 8, marginBottom: 0 }}>
                    <li>All detections are isolated incidents</li>
                    <li>Existing detections are already assigned to cases</li>
                    <li>More detections are needed to identify patterns</li>
                  </ul>
                </Alert>
              )}
            </Box>
          )}

          {correlationStatus.error && (
            <Alert severity="error">
              <Typography variant="h6" gutterBottom>
                Correlation Analysis Failed
              </Typography>
              <Typography variant="body2">
                {correlationStatus.error}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!correlationStatus.loading && (
            <Button onClick={handleCloseCorrelationModal} variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Cases;
